import React, { useState, useEffect, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash.flowright';
import { withRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import qs from 'query-string';

import Spinner from '../../../../../../components/global/Spinner';
import AlbumSlideshowGridViewer from '../../../../../../components/grid/components/AlbumSlideshowGridViewer';
import useBasename from '../../../../../../components/routing/useBasename';
import AlbumSlideshowViewerActions from '../../../../components/AlbumSlideshowViewerActions';
import useOpenModal from '../../../../../../hooks/useOpenModal';
import { getErrorString } from '../../../../../../util/normalize-errors';
import useOpenAlert from '../../../../../../hooks/useOpenAlert';
import { DISPLAY_MODE } from '../../../../../../components/grid/components/AlbumSlideshowGridViewer/AlbumSlideshowGridViewer';
import useAlbumSlideshow from '../hooks/useAlbumSlideshow';
import AlbumSlideshowOnboardingModal, {
  ALBUM_SLIDESHOW_ONBOARDING_MODAL,
} from '../../../../../../components/global/AlbumSlideshowOnboardingModal';
import { useContributorPermission } from '../../../../services/contributors';
import {
  ALBUM_SLIDESHOW_MODE,
  ALBUM_SLIDESHOW_STATUS,
} from '../../../../../../constants/albums';
import {
  isNativeAppWebView,
  postNativeAppAlbumSlideshowViewerDismiss,
  postNativeAppAlbumSlideshowViewerUpdateError,
} from '../../../../../../services/native-bridge/native-apps-interface';
import { onFullScreenToggle } from '../../../../../../util/helpers';
import useAlbumSlideshowForPendingLayouts from '../hooks/useAlbumSlideshowForPendingLayouts';
import useAlbumSlideshowForUnprocessedPhotos from '../hooks/useAlbumSlideshowForUnprocessedPhotos';
import { SORT_DIRECTION } from '../../../../../../constants/sort';
import { breakpointDown } from '../../../../../../services/responsive/responsive';
import { isIOS } from '../../../../../../util/device-detector';
import {
  isAutoLayoutsSlideshow,
  isMemoryBook,
  isWaldofied,
} from '../../../../../../util/album';
import withGoogleRecaptchaProvider from '../../../../../../components/auth/hocs/withGoogleRecaptchaProvider';
import ContributorShare from '../../../../components/ContributorShare';
import AlbumSlideshowShareModal, {
  ALBUM_SLIDESHOW_SHARE_MODAL,
} from '../../../../../../components/global/AlbumSlideshowShareModal';

const propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      slideId: PropTypes.string,
    }),
  }),
  slideshow: PropTypes.shape({
    photos: PropTypes.shape({
      total: PropTypes.number,
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
        }),
      ),
    }),
    layouts: PropTypes.shape({
      total: PropTypes.number,
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
        }),
      ),
    }),
    slide_duration_ms: PropTypes.number,
  }),
};

const AlbumSlideshowView = ({ history, location, match }) => {
  const basename = useBasename();
  const openModal = useOpenModal();
  const openAlert = useOpenAlert();
  const [saving, setSaving] = useState(false);
  const [
    checkSpecialtyAlbumRedirect,
    setCheckSpecialtyAlbumRedirectRedirect,
  ] = useState(false);
  const [displayMode, setDisplayMode] = useState(DISPLAY_MODE.PREVIEW);
  const [useFullScreen, setUseFullScreen] = useState(false);
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const isNativeView = isNativeAppWebView();
  const queryParams = qs.parse(location.search);
  const { albumId, slideId } = match.params;
  const isMobile = useMediaQuery(breakpointDown('sm'));

  useEffect(() => {
    const onFullScreenChange = async () => {
      if (document.fullscreenElement) {
        try {
          await window.screen.orientation.lock('landscape');
        } catch (e) {
          // Do nothing, just avoid crashing.
        }
        setUseFullScreen(true);
      } else {
        try {
          window.screen.orientation.unlock();
        } catch {
          // Do nothing, just avoid crashing.
        }
        setUseFullScreen(false);
      }
    };

    document.addEventListener('fullscreenchange', onFullScreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullScreenChange);
    };
  }, []);

  const {
    album,
    slideshow,
    loading,
    loadingMore,
    onLoadMore,
    onSlideshowUpdate,
  } = useAlbumSlideshow({
    albumId,
    limit: 99,
    queryProps: {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-and-network',
    },
  });

  const isMemoryBookBool = isMemoryBook(album);
  const isAutoLayout = isAutoLayoutsSlideshow(slideshow);

  const {
    totalLayouts,
    hasPendingLayouts,
    isPollingPendingLayouts,
    onStartPollingPendingLayouts,
  } = useAlbumSlideshowForPendingLayouts({
    albumId,
    pollingIntervalMs: 10000,
  });

  const {
    totalPhotos,
    totalUnprocessedPhotos,
    setHasRefetched,
  } = useAlbumSlideshowForUnprocessedPhotos({
    album,
    slideshowMode: slideshow?.mode,
    slideshowLastUploadedAt: album?.last_uploaded_at,
    pollingIntervalMs: 4000,
  });

  const { isOwner, isContributor } = useContributorPermission({
    entityId: albumId,
  });

  useEffect(() => {
    if (queryParams?.present === '1') {
      setDisplayMode(DISPLAY_MODE.PRESENT);
    }

    if (queryParams?.contribute === '1') {
      setCheckSpecialtyAlbumRedirectRedirect(true);
    }
  }, []);

  useEffect(() => {
    if (!album || !checkSpecialtyAlbumRedirect) {
      return;
    }

    if (isMemoryBook(album)) {
      history.replace(`/s/standalone-event-invite/${album.id}`);
    } else {
      openModal(ALBUM_SLIDESHOW_ONBOARDING_MODAL);
    }
  }, [checkSpecialtyAlbumRedirect, album]);

  useEffect(() => {
    // We do not want any of the process below to happen for auto layouts
    if (isAutoLayout) {
      setHasRefetched(true);
    } else if (totalPhotos > slideshow?.photos?.total && !loadingMore) {
      const direction = album.consumer_photos_sort_direction;
      onLoadMore({
        direction,
        limit: totalPhotos - slideshow.photos.total,
        offset: direction === SORT_DIRECTION.DESC ? 0 : undefined,
        updateTotals: true,
      });
      setHasRefetched(true);
    }
  }, [
    totalPhotos,
    slideshow?.photos?.total,
    totalUnprocessedPhotos,
    loadingMore,
  ]);

  useEffect(() => {
    if (
      isAutoLayout &&
      !isMemoryBookBool &&
      !!totalUnprocessedPhotos &&
      !isPollingPendingLayouts
    ) {
      // Do this so we avoid polling slideshow data with photos forever.
      setHasRefetched(true);
      onStartPollingPendingLayouts({
        // 10s
        pollingIntervalMs: 10000,
        // 3 min (Slideshow should take 2 min. max to generate slides)
        maxPollingTimeMs: 600000,
      });
    }
  }, [totalUnprocessedPhotos]);

  const slides = useMemo(
    () => (isAutoLayout ? slideshow?.layouts : slideshow?.photos),
    [isAutoLayout, slideshow?.layouts, slideshow?.photos],
  );
  const slideAccessor = useMemo(
    () => (isAutoLayout ? 'account_card' : 'photo'),
    [isAutoLayout],
  );

  if (!slideshow || loading) {
    return (
      <div className="w-100 h-100vh d-flex align-items-center justify-content-center">
        <Spinner />
      </div>
    );
  }

  if (!slideId && slideshow.intro_layout && !isAutoLayout) {
    history.replace(
      `${location.pathname}/s/${
        slideshow.intro_layout.account_card.id
      }?present=${queryParams?.present ?? 0}`,
    );
  } else if (!slideId && slides.total > 0) {
    history.replace(
      `${location.pathname}/s/${slides.records[0][slideAccessor].id}?present=${
        queryParams?.present ?? 0
      }`,
    );
  }

  const onDeletePhoto = async ({ photoId, isAssetImage }) => {
    if (saving) {
      return;
    }
    setSaving(true);
    try {
      // IsAssetImage will only really true for the intro slide.
      // therefore, we can safely rely on it to remove the intro slide.
      await onSlideshowUpdate({
        createExcludedPhotos:
          !isAssetImage && slideshow.mode === ALBUM_SLIDESHOW_MODE.ALL_PHOTOS
            ? [{ photo_id: photoId }]
            : undefined,
        deleteSelectedPhotos:
          !isAssetImage &&
          slideshow.mode === ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS
            ? [{ photo_id: photoId }]
            : undefined,
        introAssetImage: isAssetImage ? null : undefined,
      });
    } catch (err) {
      const errorString = getErrorString(err);
      if (isNativeView) {
        postNativeAppAlbumSlideshowViewerUpdateError(errorString);
      } else {
        openAlert({
          message: 'There was an error updating the slideshow.',
          info: errorString,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <AlbumSlideshowGridViewer
        album={album}
        saving={saving}
        loading={loading}
        slideshow={slideshow}
        recordId={slideId}
        records={
          slideshow.intro_layout && !isAutoLayout
            ? [slideshow.intro_layout, ...slides.records]
            : slides.records
        }
        recordsTotal={slides.total}
        loadMore={onLoadMore}
        loadingMore={loadingMore}
        shouldLoadMore={
          slides.total > slides.records.length ||
          (typeof totalLayouts !== 'undefined' &&
            totalLayouts > slides.records.length)
        }
        gridPath={`/app/${basename}/albums/${albumId}/photos`}
        displayMode={displayMode}
        onDeletePhoto={authenticated && isOwner ? onDeletePhoto : undefined}
        dismissible={
          (!useFullScreen &&
            displayMode !== DISPLAY_MODE.PRESENT &&
            history.length > 1) ||
          isNativeView
        }
        onDismiss={() => {
          if (isNativeView) {
            postNativeAppAlbumSlideshowViewerDismiss();
          } else {
            history.goBack();
          }
        }}
        renderTitle={() => {
          if (isNativeView) {
            return '';
          }
          return (
            <Fragment>
              <h3 className="w-100 text-white text-center mb-0 d-none d-md-block">
                {album?.name}
              </h3>
              <h5 className="w-100 text-white text-center mb-0 d-md-none">
                {album?.name}
              </h5>
            </Fragment>
          );
        }}
        isAutoLayout={isAutoLayout}
        hasPendingLayouts={hasPendingLayouts}
        totalUnprocessedPhotos={totalUnprocessedPhotos}
        useMobileFullScreen={isMobile && useFullScreen}
        includeWaldoLogo={!isMemoryBook(album) && !isWaldofied(album)}
        actions={() => (
          <AlbumSlideshowViewerActions
            isOwner={isOwner}
            isContributor={isContributor}
            authenticated={authenticated || isNativeView}
            onEdit={
              displayMode === DISPLAY_MODE.PRESENT || useFullScreen
                ? undefined
                : () => {
                    history.push(
                      `/app/${basename}/albums/${albumId}/slideshow/designer`,
                    );
                  }
            }
            onShare={
              displayMode === DISPLAY_MODE.PRESENT || useFullScreen
                ? undefined
                : () => openModal(ALBUM_SLIDESHOW_SHARE_MODAL)
            }
            onPresent={
              displayMode === DISPLAY_MODE.PRESENT || useFullScreen
                ? undefined
                : () => setDisplayMode(DISPLAY_MODE.PRESENT)
            }
            onFullScreenToggle={
              // iOS Mobile does *not* support Full Screen API.
              displayMode === DISPLAY_MODE.PRESENT || (isMobile && isIOS())
                ? undefined
                : () => {
                    onFullScreenToggle(document.documentElement);
                  }
            }
            isPublished={slideshow.status === ALBUM_SLIDESHOW_STATUS.PUBLISHED}
          />
        )}
      />
      <ContributorShare />
      <AlbumSlideshowShareModal albumId={albumId} />
      <AlbumSlideshowOnboardingModal albumId={albumId} />
    </Fragment>
  );
};

AlbumSlideshowView.propTypes = propTypes;

export default compose(
  withRouter,
  withGoogleRecaptchaProvider,
)(AlbumSlideshowView);
